import { ChangeLog } from 'api/Changelog/types';
import useChangelog from 'CustomHooks/useChangelog';
import useCollection from 'CustomHooks/useCollection';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { db } from 'utils/firebase';
import { asyncVoid, noop } from '../utils/helper';
import { AuthContext } from './AuthContext';

type ChangelogContextType = {
  allShowingChangelogs: ChangeLog[];
  changelogLoading: boolean;
  showChangelog: boolean;
  setshowChangelog: React.Dispatch<React.SetStateAction<boolean>>;
  updateProfilesLatestVersion: (id: string) => void;
};

interface ContextProps {
  restaurantId: null | string;
  children?: React.ReactNode;
  currentVersion?: string;
}

export const ChangelogContext = createContext<ChangelogContextType>({
  allShowingChangelogs: [],
  changelogLoading: false,
  showChangelog: false,
  setshowChangelog: noop,
  updateProfilesLatestVersion: asyncVoid,
});

// Numeric x.y.z order: 0.5.0 is newer than 0.4.210.
const compareVersions = (a: string, b: string) => {
  const x = a.split('.').map((n) => parseInt(n) || 0);
  const y = b.split('.').map((n) => parseInt(n) || 0);
  for (let i = 0; i < 3; i++) {
    if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) > (y[i] ?? 0) ? 1 : -1;
  }
  return 0;
};

const ChangelogContextProvider = ({
  restaurantId,
  children,
  currentVersion,
}: ContextProps) => {
  const { changelog, loading } = useChangelog();
  const { user } = useContext(AuthContext);

  const [showChangelog, setshowChangelog] = useState<boolean>(false);

  const changeLogs = useMemo(() => {
    return changelog.map((c) => c.fields);
  }, [changelog]);

  const showingChangelog = useMemo(() => {
    if (!user.data) return [];

    const userVersion = user.data?.appVersion ?? null;

    const VERSION = JSON.stringify(require('../../package.json').version);

    let currentVersion = '0.0.0';
    try {
      currentVersion = VERSION.slice(1, VERSION.length - 1);
    } catch (error) {
      console.log('Cannot get version of application.');
    }

    return changeLogs.filter((v) => {
      const version = v?.id ?? '0.0.0';

      if (compareVersions(version, currentVersion) > 0) {
        return false;
      }

      if (!userVersion || compareVersions(version, userVersion) >= 0) {
        return v;
      } else return false;
    });
  }, [changeLogs, user]);

  async function updateProfilesLatestVersion(id: string) {
    setshowChangelog(false);
    try {
      await db
        .collection('users')
        .doc(id)
        .update({ appVersion: currentVersion });
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    const userVersion = user?.data?.appVersion ?? null;
    if (
      !userVersion ||
      compareVersions(currentVersion ?? '0.0.0', userVersion) > 0
    )
      setshowChangelog(true);
  }, [user]);

  // console.log({ changelog });
  // console.log({ showChangelog });

  return (
    <ChangelogContext.Provider
      value={{
        allShowingChangelogs: showingChangelog,
        showChangelog: showChangelog && !!showingChangelog?.length,
        setshowChangelog,
        changelogLoading: loading,
        updateProfilesLatestVersion,
      }}
    >
      {children}
    </ChangelogContext.Provider>
  );
};

export default ChangelogContextProvider;
